import axios from 'axios';
import { API_URL } from './config';

interface IUserLoginParams {
  email: string;
  password: string;
}

interface IBaseUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  address_1: string;
  address_2: string;
  phone: string;
  country: string;
  postal_code: string;
  email_approved: boolean;
  search_visibility: boolean;
  profile_url: string;
  subscribed?: boolean;
  block_status?: boolean;
  requestedFirstName?: string;
  requestedLastName?: string;
}

export interface TutorRef {
  id: number;
  slug: string;
  userId: number;
}

export interface StudentRef {
  id: number;
  userId: number;
}

export interface ITutorUser extends IBaseUser {
  role: 'TUTOR';
  tutor: TutorRef;
  student: null;
}

export interface IStudentUser extends IBaseUser {
  role: 'STUDENT';
  tutor: null;
  student: StudentRef;
}

export interface IAdminUser extends IBaseUser {
  role: 'ADMIN' | 'SUPER_ADMIN';
  tutor: null;
  student: null;
}

export type IUser = ITutorUser | IStudentUser | IAdminUser;

/** @deprecated Use TutorRef instead */
export type Tutor = TutorRef;

export interface IUserRegisterParams {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  address_1: string;
  address_2: string;
  phone: string;
  country: string;
  postal_code: string;
  role: 'TUTOR' | 'STUDENT';
}
export const UserAPI = {
  register: async function (params: IUserRegisterParams) {
    return (
      await axios.post<IUser>(API_URL + '/user/', params, {
        withCredentials: true,
      })
    ).data;
  },
  signout: async function () {
    return (
      await axios.post<IUser>(
        API_URL + '/user/signout',
        {},
        {
          withCredentials: true,
        }
      )
    ).data;
  },

  login: async function (params: IUserLoginParams) {
    return (
      await axios.post<IUser | { twoFactorRequired: true; email: string }>(
        API_URL + '/user/login',
        params,
        {
          withCredentials: true,
        }
      )
    ).data;
  },
  verify2FA: async function (email: string, token: number) {
    return (
      await axios.post<IUser>(
        API_URL + '/user/verify-2fa',
        { email, token },
        {
          withCredentials: true,
        }
      )
    ).data;
  },
  resend2FA: async function (email: string) {
    return (
      await axios.post<{ success: boolean }>(
        API_URL + '/user/resend-2fa',
        { email },
        {
          withCredentials: true,
        }
      )
    ).data;
  },
  passwordResetRequest: async function (email: string, role?: string) {
    return (
      await axios.post<{ success: boolean; attemptsRemaining?: number }>(
        API_URL + '/user/forgot-password',
        { email, role },
        {
          withCredentials: true,
        }
      )
    ).data;
  },
  passwordResetVerify: async function (
    email: string,
    password: string,
    otp: number,
    role?: string
  ) {
    return (
      await axios.post<{ success: boolean }>(
        API_URL + '/user/reset-password',
        { email: email, password, passwordToken: otp, role },
        {
          withCredentials: true,
        }
      )
    ).data;
  },
  userData: async function () {
    return (
      await axios.get<IUser>(API_URL + '/user', {
        withCredentials: true,
      })
    ).data;
  },
  getUserById: async function (id: number) {
    return (
      await axios.get<IUser>(API_URL + '/user/' + id, {
        withCredentials: true,
      })
    ).data;
  },
  update: async function (data: Partial<Omit<IUser, 'id'>>) {
    return (
      await axios.patch<IUser>(API_URL + '/user', data, {
        withCredentials: true,
      })
    ).data;
  },
  changePassword: async function (
    currentPassword: string,
    newPassword: string
  ) {
    const data = {
      password: currentPassword,
      newPassword: newPassword,
    };
    return (
      await axios.patch(API_URL + '/user/updatePassword', data, {
        withCredentials: true,
      })
    ).data;
  },
  uploadImage: async function (file: File): Promise<string> {
    const f = new FormData();
    f.append('file', file);
    return (
      await axios.post(API_URL + '/file', f, {
        withCredentials: true,
      })
    ).data.url;
  },
  resendVerificationEmail: async function () {
    const response = await axios.patch(
      API_URL + '/user/resend-confirmation-email',
      {},
      { withCredentials: true }
    );
    return response.data;
  },
  delete: async function () {
    const response = await axios.delete(API_URL + '/user', {
      withCredentials: true,
    });
    return response.data;
  },
  requestNameAndDocumentsUpdationApproval: async function (data: {
    documents?:{type:string,url:string}[] 
    first_name?: string;
    last_name?: string;
  }) {
   const response = await axios.patch(API_URL + '/user/request-update-approval', data, {
      withCredentials: true,
    });
    return response.data;
  },
};
